import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
	isBillableSplitAllowed,
	isTimeRegistrationNoteRequired,
} from '../../../../../shared/util/cache/TimeRegistrationSettingsUtil';
import {isTimeRegistrationNoteInvalid} from '../../../../../shared/util/time-registration/time-registration-settings/TimeRegistrationNoteFilter';
import {workingHourForTheDay} from '../../../timesheets_person_data';
import AdvancedTimeRegistrationContent, {PopupFocusItem, TimeRegistrationFocusType} from './AdvancedTimeRegistrationContent';
import {Popup} from './AdvancedTimeRegistrationStyled';
import {trackEvent} from '../../../../../../tracking/amplitude/TrackingV2';
import {useTaskEstimateValidation} from '../../../../../shared/util/time-registration/time-registration-settings/useTaskEstimateValidation';
import {FormattedMessage, useIntl} from 'react-intl';
import {Role} from '../../../../../shared/components/modals/time-registration/time-registration-modal/types/TimeRegistrationRole';
import {Button, FlexRow, PopoverHandler} from '@forecast-it/design-system';
import Spinner from '../../../../../shared/components/spinner/Spinner';
import {useSpinner} from '../../../../../shared/components/spinner/useSpinner';

interface CreateAdvancedTimeRegistrationProps {
	billable: boolean;
	selectedPerson: Record<string, unknown>;
	dayOfInput: Date;
	mutation: (
		hours: number | null,
		billableHours: number | undefined | null,
		notes: string | undefined,
		roleId: string | undefined,
		onSuccess: () => void
	) => void;
	taskTotalMinutesRegistered: number | null;
	taskEstimateForecastMinutes: number | null;
	canHaveRole: boolean;
	projectId?: string;
	taskId?: string;
	isSuggestion?: boolean;
}

function CreateAdvancedTimeRegistrationContent({
	billable,
	mutation,
	selectedPerson,
	dayOfInput,
	taskEstimateForecastMinutes,
	taskTotalMinutesRegistered,
	canHaveRole,
	projectId,
	taskId,
	isSuggestion,
}: CreateAdvancedTimeRegistrationProps): JSX.Element {
	useEffect(() => {
		trackEvent('Create Advanced Time Registration Popup', 'Opened');
	}, []);
	const intl = useIntl();
	const id = 'time-reg-popup';
	const {setOpen} = PopoverHandler.usePopoverState();
	const [waitingForMutation, setWaitingForMutation] = useState(false);
	const {spinning} = useSpinner({waiting: waitingForMutation});

	const [timeInputMinutes, setTimeInputMinutes] = useState(0);
	const [billableTimeInputMinutes, setBillableTimeInputMinutes] = useState<number | null>(null);
	const [notes, setNotes] = useState('');
	const [role, setRole] = useState<Role | null>(null);

	const [noteError, setNoteError] = useState(false);
	const [timeRegistrationFocus, setTimeRegistrationFocus] = useState<TimeRegistrationFocusType>({
		id: null,
		focusItem: null,
	}); // Used to propagate focus control down to children
	const [timeError, setTimeError] = useState<string | null>(null);

	const addButtonRef = useRef<HTMLButtonElement>(null);

	const isNoteRequired = isTimeRegistrationNoteRequired();
	const {remainingInfoMessage, validateTimeInputValue} = useTaskEstimateValidation(
		taskEstimateForecastMinutes,
		taskTotalMinutesRegistered,
		0,
		intl
	);

	const onTimeChange = useCallback(
		(minutes: number, isEnterBlur: boolean) => {
			setTimeInputMinutes(minutes);
			if (isEnterBlur) {
				addButtonRef.current?.focus();
			}
		},
		[setTimeInputMinutes, addButtonRef.current]
	);

	const onBillableTimeChange = useCallback(
		(minutes: number | null, isEnterBlur: boolean) => {
			setBillableTimeInputMinutes(minutes);
			if (isEnterBlur) {
				addButtonRef.current?.focus();
			}
		},
		[setBillableTimeInputMinutes, addButtonRef.current]
	);

	const onNotesChange = useCallback(
		notes => {
			setNotes(notes);
			setNoteError(false);
		},
		[setNotes, setNoteError]
	);

	const onRoleChange = useCallback(
		role => {
			setRole(role);
		},
		[setRole]
	);

	const handleAddButtonClick = () => {
		setWaitingForMutation(true);
		const timeInputError = validateTimeInputValue(timeInputMinutes);
		if (timeInputError) {
			setTimeError(timeInputError);
			setTimeRegistrationFocus({id: id, focusItem: PopupFocusItem.Hours});
			setWaitingForMutation(false);
			return;
		}

		const noteInvalid = isNoteRequired && isTimeRegistrationNoteInvalid(notes);
		if (noteInvalid) {
			setNoteError(true);
			setTimeRegistrationFocus({id: id, focusItem: PopupFocusItem.Notes});
			setWaitingForMutation(false);
			return;
		}

		const hours = timeInputMinutes ? timeInputMinutes / 60 : 0;
		// If the popout is used for a non-billable time registration we don't want to send any value (not even null) to the mutation
		// since we want to preserve the existing billableMinutesRegistered in the db in case the item is made billable in the future.
		const billableHours = billable
			? billableTimeInputMinutes != null
				? billableTimeInputMinutes / 60
				: undefined
			: undefined;

		function onSuccess() {
			setWaitingForMutation(false);
			setOpen(false);
			trackEvent('Advanced Time Registration', 'Created', {
				billable: billable,
				minutesRegistered: timeInputMinutes,
				billableMinutesRegistered: billableTimeInputMinutes,
				role: role?.name,
				notes: notes,
				isSuggestion: !!isSuggestion,
			});
		}

		mutation(hours, billableHours, notes ? notes : undefined, role?.id, onSuccess);
	};

	const workingHours = useMemo(() => workingHourForTheDay(selectedPerson, dayOfInput), [selectedPerson, dayOfInput]);

	const makeWider = isBillableSplitAllowed() && billable && canHaveRole;

	return (
		<Popup makeWider={makeWider}>
			<AdvancedTimeRegistrationContent
				id={id}
				timeInputMinutes={timeInputMinutes}
				onTimeChange={onTimeChange}
				billableTimeInputMinutes={billableTimeInputMinutes}
				onBillableTimeChange={onBillableTimeChange}
				notes={notes}
				onNotesChange={onNotesChange}
				billable={billable}
				noteError={noteError}
				timeError={timeError}
				remainingInfoMessage={remainingInfoMessage}
				workingHours={workingHours}
				timeRegistrationFocus={timeRegistrationFocus}
				onRoleChange={onRoleChange}
				firstTimeRegistration={true}
				canHaveRole={canHaveRole}
				projectId={projectId}
				taskId={taskId}
				personId={selectedPerson.id + ''}
			/>
			<FlexRow justifyContent="flex-end">
				<Button
					ref={addButtonRef}
					data-cy="add-new-time-entry-button"
					onPress={handleAddButtonClick}
					disabled={waitingForMutation || timeInputMinutes == null}
				>
					<Spinner spinning={spinning}>
						<FormattedMessage id="common.add" />
					</Spinner>
				</Button>
			</FlexRow>
		</Popup>
	);
}

export default CreateAdvancedTimeRegistrationContent;
